import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Type } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { FilterListSearchComponent, ListLayout, ListLayoutMultipleComponent, SearchField } from 'gung-list';
import { first, forkJoin, Observable, of, switchMap } from 'rxjs';
import { ProductGridViewV2Component } from '../components/product-grid-view-v2/product-grid-view-v2.component';
import { ProductListViewV2Component } from '../components/product-list-view-v2/product-list-view-v2.component';
import { Product } from '../models';
import { NavigationConfig } from '../models/navigation-config';
import { AuthService } from './auth/auth.service';
import { CurrentAssortmentService } from './base-view-config/base-view-config.service';
import { GungFlowService } from './gung-flow/gung-flow.service';
import { FilterSelected, GungBackendConfigBaseFilter, GungBackendFilter, GungBackendFilterItems, GungBackendFilterListConfigService, SimpleGungBackendConfigBaseFilter } from '../models/gung-backend-filter-list';

@Injectable({
  providedIn: 'root'
})
export class ProductListBackendConfigService implements GungBackendFilterListConfigService<Product> {
  topFilter = true;
  sourceClass: string = 'io.gung.model.Product';

  constructor(
    protected authService: AuthService,
    protected gungFlowService: GungFlowService,
    protected currentAssortmentService: CurrentAssortmentService,
    protected http: HttpClient,
    @Inject('environment')
    protected environment: NavigationConfig
  ) {}

  getBackendFilters(): Observable<GungBackendConfigBaseFilter<Product>[]> {
    const filters = [new ProdPostingGroupFilter(), new BlockedFilter()];
    return of(filters);
  }

  getExtraBackendFilterParams(): Observable<{ [s: string]: any; }> {
    return of();
  }

  getItems(
    skip: number,
    limit: number,
    searchString: string,
    filterSelected: FilterSelected[],
    user: any,
    flow: any,
    routeSnapshot: ActivatedRouteSnapshot
  ): Observable<GungBackendFilterItems<Product>> {
    return this.getBackendFilters().pipe(
      first(),
      switchMap(filters => {
        const filterSelection = {};
        for (const filter of filters) {
          filterSelection[
            `io.gung.model.Product@${filter.field}?assortmentId=${user.assortment}&flowId=${flow.id}&stockId=${user.managedMultistockIds[0]}`
          ] = [];
        }

        if (!!filterSelected && filterSelected.length > 0) {
          for (const filter of filterSelected) {
            if (!filter.filterId.includes('io.gung.model.Product')) {
              filter.filterId = `io.gung.model.Product@${filter.filterId}?assortmentId=${user.assortment}&flowId=${flow.id}&stockId=${user.managedMultistockIds[0]}`
            }
            filterSelection[filter.filterId].push(filter.optionId !== 'UNDEFINED' ? filter.optionId.toString() : '')
          }
        }

        return this.http.post<GungBackendFilterItems<Product>>('json/backend-filters-test', {
          skip: skip,
          limit: limit,
          searchString: searchString,
          filterSelection
        });
      })
    );
  }

  getSearchTerms(item: Product) {
    return [item.id, item.name];
  }

  getSearchTermFields() {
    return ['id', 'name'];
  }

  getDefaultSearchField(): SearchField<Product> {
    return {
      getComponent: () => {
        return this.getSearchComponent();
      },
      getKey: () => {
        return 'DEFAULT';
      },
      getSearchTerms: (item: Product) => this.getSearchTerms(item),
      getPlaceHolder: () => this.getSearchPlaceholderTranslate(),
      componentClass: () => 'col-12'
    };
  }

  getBatchSizes(): number[] {
    if (this.environment?.standardErp) {
      return [24];
    }
    return [24, 48];
  }

  getLayouts(): ListLayout<Product>[] {
    const currentAssortment = this.currentAssortmentService.currentAssortment;
    if (currentAssortment && currentAssortment.extra.pim && currentAssortment.extra.pim.showAsList) {
      return [
        {
          getIconClass: () => 'fa fa-list',
          getListItemComponent: () => ProductListViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductGrid'
        },
        {
          getIconClass: () => 'fa fa-th',
          getListItemComponent: () => ProductGridViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductGrid'
        }
      ];
    } else {
      return [
        {
          getIconClass: () => 'fa fa-th',
          getListItemComponent: () => ProductGridViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductGrid'
        },
        {
          getIconClass: () => 'fa fa-list',
          getListItemComponent: () => ProductListViewV2Component,
          getListLayoutComponent: () => ListLayoutMultipleComponent,
          getName: () => 'ProductGrid'
        }
      ];
    }
  }

  getItemId(item: Product): string {
    return item.id;
  }

  getSearchComponent(): Type<FilterListSearchComponent> {
    return FilterListSearchComponent;
  }

  getSearchPlaceholderTranslate(): string {
    return 'SEARCH';
  }
}
export class ProdPostingGroupFilter extends SimpleGungBackendConfigBaseFilter<GungBackendFilterItems<Product>> {
  field = 'extra.gungItems.genProdPostingGroup';

  getName(): string {
    return 'POSTING_GROUP';
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class BlockedFilter extends SimpleGungBackendConfigBaseFilter<GungBackendFilterItems<Product>> {
  field = 'extra.gungItems.blocked';

  getName(): string {
    return 'BLOCKED';
  }

  getOptionName(key: string): string {
    return key;
  }
}
